// frontend/src/features/campaign/utils/campaignKpiCalculator.js

export const DEFAULT_SCENARIO_RATES = {
  conservative: {
    replyRate: 6.0,
    positiveReplyRate: 40.0,
    meetingRate: 55.0,
    opportunityRate: 30.0,
    label: 'Conservative (0.75x)',
  },
  baseline: {
    replyRate: 8.5,
    positiveReplyRate: 45.0,
    meetingRate: 65.0,
    opportunityRate: 35.0,
    label: 'Baseline AI Model (1.0x)',
  },
  optimistic: {
    replyRate: 12.0,
    positiveReplyRate: 52.0,
    meetingRate: 72.0,
    opportunityRate: 42.0,
    label: 'Optimistic (1.35x)',
  },
};

/**
 * Parses numeric currency value from string or number.
 * e.g. "RM 25,000" -> 25000
 */
export function parseCurrencyNumber(val) {
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (!val) return 0;
  const cleaned = String(val).replace(/[^0-9.]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Calculates campaign KPIs, conversion counts, ranges, and financial ROI metrics.
 */
export function calculateCampaignKPIs({
  audience = 0,
  reachable = null,
  scenario = 'baseline',
  avgDealSize = 35000,
  allocatedBudget = 25000,
  customRates = null,
}) {
  const cleanAudience = Math.max(0, parseInt(audience, 10) || 0);
  const cleanReachable =
    reachable !== null && reachable !== undefined
      ? Math.max(0, parseInt(reachable, 10) || 0)
      : Math.round(cleanAudience * 0.82);

  const cleanDealSize = Math.max(0, parseCurrencyNumber(avgDealSize));
  const cleanBudget = Math.max(0, parseCurrencyNumber(allocatedBudget));

  const scenarioKey = DEFAULT_SCENARIO_RATES[scenario] ? scenario : 'baseline';
  const defaultRates = DEFAULT_SCENARIO_RATES[scenarioKey];

  const replyRate =
    customRates?.replyRate !== undefined && customRates?.replyRate !== null
      ? parseFloat(customRates.replyRate) || 0
      : defaultRates.replyRate;

  const positiveReplyRate =
    customRates?.positiveReplyRate !== undefined && customRates?.positiveReplyRate !== null
      ? parseFloat(customRates.positiveReplyRate) || 0
      : defaultRates.positiveReplyRate;

  const meetingRate =
    customRates?.meetingRate !== undefined && customRates?.meetingRate !== null
      ? parseFloat(customRates.meetingRate) || 0
      : defaultRates.meetingRate;

  const opportunityRate =
    customRates?.opportunityRate !== undefined && customRates?.opportunityRate !== null
      ? parseFloat(customRates.opportunityRate) || 0
      : defaultRates.opportunityRate;

  if (cleanAudience === 0 || cleanReachable === 0) {
    return {
      scenario: scenarioKey,
      rates: {
        replyRate,
        positiveReplyRate,
        meetingRate,
        opportunityRate,
      },
      counts: {
        audience: cleanAudience,
        reachable: cleanReachable,
        replies: 0,
        positiveReplies: 0,
        meetings: 0,
        opportunities: 0,
      },
      audience: 0,
      reachable: 0,
      replies: '0',
      positiveReplies: '0',
      meetings: '0',
      opportunities: 0,
      pipelineValue: 'RM 0',
      pipelineRange: 'RM 0',
      estimatedPipelineRaw: 0,
      financials: {
        avgDealSize: cleanDealSize,
        allocatedBudget: cleanBudget,
        pipelineValue: 0,
        formattedPipelineValue: 'RM 0',
        roiMultiple: 'N/A',
        costPerMeeting: 0,
        formattedCostPerMeeting: 'N/A',
        costPerOpportunity: 0,
        formattedCostPerOpportunity: 'N/A',
      },
    };
  }

  // Funnel calculations
  const replies = Math.round(cleanReachable * (replyRate / 100));
  const positiveReplies = Math.round(replies * (positiveReplyRate / 100));
  const meetings = Math.round(positiveReplies * (meetingRate / 100));
  const opportunities =
    meetings > 0 ? Math.max(1, Math.round(meetings * (opportunityRate / 100))) : 0;
  const pipelineValue = opportunities * cleanDealSize;

  // Ranges (proportional ±20% clamped)
  const repliesRange =
    replies > 0 ? `${Math.max(1, Math.round(replies * 0.8))} - ${Math.round(replies * 1.25)}` : '0';
  const positiveRepliesRange =
    positiveReplies > 0
      ? `${Math.max(1, Math.round(positiveReplies * 0.8))} - ${Math.round(positiveReplies * 1.25)}`
      : '0';
  const meetingsRange =
    meetings > 0
      ? `${Math.max(1, Math.round(meetings * 0.8))} - ${Math.round(meetings * 1.25)}`
      : '0';
  const opportunitiesRange =
    opportunities > 0
      ? `${Math.max(1, Math.round(opportunities * 0.8))} - ${Math.round(opportunities * 1.25)}`
      : '0';

  const pipelineMin =
    opportunities > 0 ? Math.max(1, Math.round(opportunities * 0.8)) * cleanDealSize : 0;
  const pipelineMax = opportunities > 0 ? Math.round(opportunities * 1.25) * cleanDealSize : 0;
  const pipelineRange =
    opportunities > 0
      ? `RM ${pipelineMin.toLocaleString()} - RM ${pipelineMax.toLocaleString()}`
      : 'RM 0';

  // Financial ROI metrics
  const roiMultiple = cleanBudget > 0 ? (pipelineValue / cleanBudget).toFixed(1) + 'x' : 'N/A';
  const costPerMeeting = meetings > 0 && cleanBudget > 0 ? Math.round(cleanBudget / meetings) : 0;
  const costPerOpportunity =
    opportunities > 0 && cleanBudget > 0 ? Math.round(cleanBudget / opportunities) : 0;

  return {
    scenario: scenarioKey,
    rates: {
      replyRate,
      positiveReplyRate,
      meetingRate,
      opportunityRate,
    },
    counts: {
      audience: cleanAudience,
      reachable: cleanReachable,
      replies,
      positiveReplies,
      meetings,
      opportunities,
    },
    // Top-level convenience properties for backward compatibility
    audience: cleanAudience,
    reachable: cleanReachable,
    replies: repliesRange,
    positiveReplies: positiveRepliesRange,
    meetings: meetingsRange,
    opportunities: opportunitiesRange,
    pipelineValue: cleanDealSize > 0 ? `RM ${pipelineValue.toLocaleString()}` : 'RM 0',
    pipelineRange,
    estimatedPipelineRaw: pipelineValue,
    financials: {
      avgDealSize: cleanDealSize,
      allocatedBudget: cleanBudget,
      pipelineValue,
      formattedPipelineValue: cleanDealSize > 0 ? `RM ${pipelineValue.toLocaleString()}` : 'RM 0',
      roiMultiple,
      costPerMeeting,
      formattedCostPerMeeting: costPerMeeting > 0 ? `RM ${costPerMeeting.toLocaleString()}` : 'N/A',
      costPerOpportunity,
      formattedCostPerOpportunity:
        costPerOpportunity > 0 ? `RM ${costPerOpportunity.toLocaleString()}` : 'N/A',
    },
  };
}
